import type { Command, CommandResponse } from '../../core/commands/types'
import type { Scenario, List, Item, Flow, ScenarioItem } from '../../core/domain/models'
import { generateCommandId } from '../../core/commands/utils'
import { getCurrentISOTimestamp } from '../../core/utils/timestamp'
import type { ToastType } from './toast-store'

export interface CommandNotificationContext {
  getScenario: (scenarioId: string) => Scenario | undefined
  getList: (listId: string) => List | undefined
  getItem: (listId: string, itemId: string) => Item | undefined
  getItemsByList: (listId: string) => Item[]
  getFlow: (flowId: string) => Flow | undefined
  getScenarioItems: (scenarioId: string) => ScenarioItem[]
}

export interface CommandUndoPlan {
  label: string
  buildCommands: () => Command[]
}

export interface CommandNotificationPlan {
  message: string
  itemName?: string
  type?: ToastType
  duration?: number
  undo?: CommandUndoPlan
  errorMessage?: string
}

export interface CommandPresentation {
  uiMetadata: {
    entityName?: string
    description?: string
  }
  notification: CommandNotificationPlan
}

const DEFAULT_ERROR_MESSAGE = 'Command failed'

function cloneDeep<T>(value: T): T {
  if (value === undefined || value === null) return value
  return JSON.parse(JSON.stringify(value)) as T
}

function safeScenarioName(scenario?: Scenario): string | undefined {
  return scenario?.name
}

function safeListName(list?: List): string | undefined {
  return list?.name
}

function safeItemName(item?: Item): string | undefined {
  return item?.name
}

function safeFlowName(flow?: Flow): string | undefined {
  return flow?.name
}

function buildUndoCommand(base: Record<string, unknown>): Command {
  return {
    ...base,
    isUndo: true,
    commandId: generateCommandId(),
    clientTimestamp: getCurrentISOTimestamp()
  } as Command
}

type CommandPresentationMap = {
  [K in Command['commandType']]: (
    command: Extract<Command, { commandType: K }>,
    context: CommandNotificationContext
  ) => CommandPresentation
}

const commandPresentationMap: CommandPresentationMap = {
  CompleteItem: (command, context) => {
    const item = cloneDeep(context.getItem(command.listId, command.itemId))
    const itemName = safeItemName(item)
    const message = command.isComplete ? 'completed' : 'restored'

    return {
      uiMetadata: { entityName: itemName, description: command.isComplete ? 'Marked as completed' : 'Marked as active' },
      notification: {
        message,
        itemName,
        type: 'success',
        duration: 5000,
        errorMessage: 'Could not update item status',
        undo: {
          label: 'Undo',
          buildCommands: () => [
            buildUndoCommand({
              commandType: 'CompleteItem',
              scenarioId: command.scenarioId,
              listId: command.listId,
              itemId: command.itemId,
              isComplete: !command.isComplete
            })
          ]
        }
      }
    }
  },
  RenameItem: (command, context) => {
    const item = cloneDeep(context.getItem(command.listId, command.itemId))
    const previousName = safeItemName(item)

    return {
      uiMetadata: {
        entityName: command.newName,
        description: previousName ? `Renamed from "${previousName}"` : 'Renamed item'
      },
      notification: {
        message: `Renamed item to "${command.newName}"`,
        type: 'success',
        errorMessage: 'Could not rename item',
        undo: previousName
          ? {
              label: 'Undo',
              buildCommands: () => [
                buildUndoCommand({
                  commandType: 'RenameItem',
                  scenarioId: command.scenarioId,
                  listId: command.listId,
                  itemId: command.itemId,
                  newName: previousName
                })
              ]
            }
          : undefined
      }
    }
  },
  ReorderItem: (command, context) => {
    const item = cloneDeep(context.getItem(command.listId, command.itemId))
    const itemName = safeItemName(item)
    const previousOrder = item?.order

    return {
      uiMetadata: { entityName: itemName, description: 'Reordered item' },
      notification: {
        message: 'moved',
        itemName,
        type: 'success',
        errorMessage: 'Could not reorder item',
        undo: typeof previousOrder === 'number'
          ? {
              label: 'Undo',
              buildCommands: () => [
                buildUndoCommand({
                  commandType: 'ReorderItem',
                  scenarioId: command.scenarioId,
                  listId: command.listId,
                  itemId: command.itemId,
                  newOrder: previousOrder
                })
              ]
            }
          : undefined
      }
    }
  },
  CreateItem: (command) => {
    return {
      uiMetadata: { entityName: command.itemName, description: 'Created item' },
      notification: {
        message: 'added',
        itemName: command.itemName,
        type: 'success',
        errorMessage: 'Could not add item',
        undo: {
          label: 'Undo',
          buildCommands: () => [
            buildUndoCommand({
              commandType: 'DeleteItem',
              scenarioId: command.scenarioId,
              listId: command.listId,
              itemId: command.itemId
            })
          ]
        }
      }
    }
  },
  CreateItems: (command) => {
    return {
      uiMetadata: {
        entityName: command.items.map(i => i.itemName).join(', '),
        description: `Created ${command.items.length} items`
      },
      notification: {
        message: `Added ${command.items.length} items`,
        type: 'success',
        errorMessage: 'Could not add items',
        undo: {
          label: 'Undo',
          buildCommands: () =>
            command.items.map(item =>
              buildUndoCommand({
                commandType: 'DeleteItem',
                scenarioId: command.scenarioId,
                listId: command.listId,
                itemId: item.itemId
              })
            )
        }
      }
    }
  },
  UpdateItemQuantity: (command, context) => {
    const item = cloneDeep(context.getItem(command.listId, command.itemId))
    const itemName = safeItemName(item)
    const previousQuantity = item?.quantity

    return {
      uiMetadata: { entityName: itemName, description: 'Updated quantity' },
      notification: {
        message: 'updated quantity',
        itemName,
        type: 'success',
        errorMessage: 'Could not update quantity',
        undo: typeof previousQuantity === 'number'
          ? {
              label: 'Undo',
              buildCommands: () => [
                buildUndoCommand({
                  commandType: 'UpdateItemQuantity',
                  scenarioId: command.scenarioId,
                  listId: command.listId,
                  itemId: command.itemId,
                  quantity: previousQuantity,
                  updateMetaItem: command.updateMetaItem
                })
              ]
            }
          : undefined
      }
    }
  },
  DeleteItem: (command, context) => {
    const item = cloneDeep(context.getItem(command.listId, command.itemId))
    const itemName = safeItemName(item)

    return {
      uiMetadata: { entityName: itemName, description: 'Deleted item' },
      notification: {
        message: 'deleted',
        itemName,
        type: 'delete',
        duration: 5000,
        errorMessage: 'Could not delete item',
        undo: item
          ? {
              label: 'Undo',
              buildCommands: () => [
                buildUndoCommand({
                  commandType: 'CreateItem',
                  scenarioId: command.scenarioId,
                  listId: command.listId,
                  itemId: item.id,
                  itemName: item.name,
                  order: item.order
                })
              ]
            }
          : undefined
      }
    }
  },
  CreateList: (command) => {
    return {
      uiMetadata: { entityName: command.listName, description: 'Created list' },
      notification: {
        message: 'created list',
        itemName: command.listName,
        type: 'success',
        errorMessage: 'Could not create list',
        undo: {
          label: 'Undo',
          buildCommands: () => [
            buildUndoCommand({
              commandType: 'DeleteList',
              scenarioId: command.scenarioId,
              listId: command.listId
            })
          ]
        }
      }
    }
  },
  RenameList: (command, context) => {
    const list = cloneDeep(context.getList(command.listId))
    const previousName = safeListName(list)

    return {
      uiMetadata: {
        entityName: command.newName,
        description: previousName ? `Renamed from "${previousName}"` : 'Renamed list'
      },
      notification: {
        message: `Renamed list to "${command.newName}"`,
        type: 'success',
        errorMessage: 'Could not rename list',
        undo: previousName
          ? {
              label: 'Undo',
              buildCommands: () => [
                buildUndoCommand({
                  commandType: 'RenameList',
                  scenarioId: command.scenarioId,
                  listId: command.listId,
                  newName: previousName,
                  newDescription: list?.description
                })
              ]
            }
          : undefined
      }
    }
  },
  DeleteList: (command, context) => {
    const list = cloneDeep(context.getList(command.listId))
    const listName = safeListName(list)
    const listItems = list?.items ? Object.values(list.items) : []
    const completedItems = listItems.filter(i => i.isCompleted)

    return {
      uiMetadata: { entityName: listName, description: 'Deleted list' },
      notification: {
        message: 'deleted list',
        itemName: listName,
        type: 'delete',
        duration: 5000,
        errorMessage: 'Could not delete list',
        undo: list
          ? {
              label: 'Undo',
              buildCommands: () => {
                const commands: Command[] = []

                commands.push(
                  buildUndoCommand({
                    commandType: 'CreateList',
                    scenarioId: command.scenarioId,
                    listId: list.id,
                    listName: list.name,
                    description: list.description
                  })
                )

                if (listItems.length > 0) {
                  commands.push(
                    buildUndoCommand({
                      commandType: 'CreateItems',
                      scenarioId: command.scenarioId,
                      listId: list.id,
                      items: listItems.map(i => ({
                        itemId: i.id,
                        itemName: i.name,
                        order: i.order
                      }))
                    })
                  )
                }

                for (const completedItem of completedItems) {
                  commands.push(
                    buildUndoCommand({
                      commandType: 'CompleteItem',
                      scenarioId: command.scenarioId,
                      listId: list.id,
                      itemId: completedItem.id,
                      isComplete: true
                    })
                  )
                }

                return commands
              }
            }
          : undefined
      }
    }
  },
  CreateFlow: (command) => {
    return {
      uiMetadata: { entityName: command.flowName, description: 'Created flow' },
      notification: {
        message: 'created flow',
        itemName: command.flowName,
        type: 'success',
        errorMessage: 'Could not create flow',
        undo: {
          label: 'Undo',
          buildCommands: () => [
            buildUndoCommand({
              commandType: 'DeleteFlow',
              scenarioId: command.scenarioId,
              flowId: command.flowId
            })
          ]
        }
      }
    }
  },
  RenameFlow: (command, context) => {
    const flow = cloneDeep(context.getFlow(command.flowId))
    const previousName = safeFlowName(flow)

    return {
      uiMetadata: {
        entityName: command.newName,
        description: previousName ? `Renamed from "${previousName}"` : 'Renamed flow'
      },
      notification: {
        message: `Renamed flow to "${command.newName}"`,
        type: 'success',
        errorMessage: 'Could not rename flow',
        undo: previousName
          ? {
              label: 'Undo',
              buildCommands: () => [
                buildUndoCommand({
                  commandType: 'RenameFlow',
                  scenarioId: command.scenarioId,
                  flowId: command.flowId,
                  newName: previousName,
                  newDescription: flow?.description
                })
              ]
            }
          : undefined
      }
    }
  },
  DeleteFlow: (command, context) => {
    const flow = cloneDeep(context.getFlow(command.flowId))
    const scenario = cloneDeep(context.getScenario(command.scenarioId))
    const flowName = safeFlowName(flow)
    const rankings = flow?.rankings ?? {}
    const hasRankings = Object.keys(rankings).length > 0
    const wasDefault = !!scenario?.defaultFlowId && scenario.defaultFlowId === command.flowId

    return {
      uiMetadata: { entityName: flowName, description: 'Deleted flow' },
      notification: {
        message: 'deleted flow',
        itemName: flowName,
        type: 'delete',
        duration: 5000,
        errorMessage: 'Could not delete flow',
        undo: flow
          ? {
              label: 'Undo',
              buildCommands: () => {
                const commands: Command[] = [
                  buildUndoCommand({
                    commandType: 'CreateFlow',
                    scenarioId: command.scenarioId,
                    flowId: flow.id,
                    flowName: flow.name,
                    description: flow.description
                  })
                ]

                if (wasDefault) {
                  commands.push(
                    buildUndoCommand({
                      commandType: 'SetDefaultFlow',
                      scenarioId: command.scenarioId,
                      flowId: flow.id
                    })
                  )
                }

                if (hasRankings) {
                  commands.push(
                    buildUndoCommand({
                      commandType: 'UpdateFlowRankings',
                      scenarioId: command.scenarioId,
                      flowId: flow.id,
                      rankings
                    })
                  )
                }

                return commands
              }
            }
          : undefined
      }
    }
  },
  UpdateFlowRankings: (command, context) => {
    const flow = cloneDeep(context.getFlow(command.flowId))
    const flowName = safeFlowName(flow)
    const previousRankings = flow?.rankings

    return {
      uiMetadata: { entityName: flowName ?? 'Flow', description: 'Updated flow rankings' },
      notification: {
        message: 'updated flow',
        itemName: flowName,
        type: 'success',
        errorMessage: 'Could not update flow rankings',
        undo: previousRankings
          ? {
              label: 'Undo',
              buildCommands: () => [
                buildUndoCommand({
                  commandType: 'UpdateFlowRankings',
                  scenarioId: command.scenarioId,
                  flowId: command.flowId,
                  rankings: previousRankings
                })
              ]
            }
          : undefined
      }
    }
  },
  SetDefaultFlow: (command, context) => {
    const flow = cloneDeep(context.getFlow(command.flowId))
    const flowName = safeFlowName(flow)

    return {
      uiMetadata: { entityName: flowName ?? 'Flow', description: 'Set as default flow' },
      notification: {
        message: 'set as default',
        itemName: flowName,
        type: 'success',
        errorMessage: 'Could not set default flow'
      }
    }
  },
  SetItemFlowPreferences: (_command) => {
    return {
      uiMetadata: { entityName: 'Item', description: 'Updated item flow preferences' },
      notification: {
        message: 'updated preferences',
        itemName: 'Item',
        type: 'success',
        errorMessage: 'Could not update item flow preferences'
      }
    }
  },
  CreateScenario: (command) => {
    return {
      uiMetadata: { entityName: command.name, description: 'Created scenario' },
      notification: {
        message: 'created scenario',
        itemName: command.name,
        type: 'success',
        errorMessage: 'Could not create scenario',
        undo: {
          label: 'Undo',
          buildCommands: () => [
            buildUndoCommand({
              commandType: 'DeleteScenario',
              scenarioId: command.scenarioId
            })
          ]
        }
      }
    }
  },
  RenameScenario: (command, context) => {
    const scenario = cloneDeep(context.getScenario(command.scenarioId))
    const previousName = safeScenarioName(scenario)

    return {
      uiMetadata: {
        entityName: command.newName,
        description: previousName ? `Renamed from "${previousName}"` : 'Renamed scenario'
      },
      notification: {
        message: `Renamed scenario to "${command.newName}"`,
        type: 'success',
        errorMessage: 'Could not rename scenario',
        undo: previousName
          ? {
              label: 'Undo',
              buildCommands: () => [
                buildUndoCommand({
                  commandType: 'RenameScenario',
                  scenarioId: command.scenarioId,
                  newName: previousName,
                  newDescription: scenario?.description
                })
              ]
            }
          : undefined
      }
    }
  },
  DeleteScenario: (command, context) => {
    const scenario = cloneDeep(context.getScenario(command.scenarioId))
    const scenarioName = safeScenarioName(scenario)

    return {
      uiMetadata: { entityName: scenarioName, description: 'Deleted scenario' },
      notification: {
        message: 'deleted scenario',
        itemName: scenarioName,
        type: 'delete',
        errorMessage: 'Could not delete scenario'
      }
    }
  },
  LeaveScenario: (command, context) => {
    const scenario = cloneDeep(context.getScenario(command.scenarioId))
    const scenarioName = safeScenarioName(scenario)

    return {
      uiMetadata: { entityName: scenarioName, description: 'Left scenario' },
      notification: {
        message: 'left scenario',
        itemName: scenarioName,
        type: 'delete',
        errorMessage: 'Could not leave scenario'
      }
    }
  },
  DeleteScenarioItems: (command, context) => {
    const scenario = cloneDeep(context.getScenario(command.scenarioId))
    const scenarioName = safeScenarioName(scenario)

    return {
      uiMetadata: { entityName: scenarioName ?? 'Scenario Items', description: `Deleted ${command.itemIds.length} items` },
      notification: {
        message: `Deleted ${command.itemIds.length} scenario items`,
        type: 'delete',
        errorMessage: 'Could not delete scenario items'
      }
    }
  },
  AddScenarioItems: (command, context) => {
    const scenario = cloneDeep(context.getScenario(command.scenarioId))
    const scenarioName = safeScenarioName(scenario)

    return {
      uiMetadata: { entityName: scenarioName ?? 'Scenario Items', description: `Added ${command.itemNames.length} items` },
      notification: {
        message: `Imported ${command.itemNames.length} items`,
        type: 'success',
        errorMessage: 'Could not import items'
      }
    }
  }
}

export function buildCommandPresentation(command: Command, context: CommandNotificationContext): CommandPresentation {
  return commandPresentationMap[command.commandType](command as any, context)
}

export function buildCommandNotification(command: Command, context: CommandNotificationContext): CommandNotificationPlan {
  return buildCommandPresentation(command, context).notification
}

export function resolveErrorMessage(command: Command, context: CommandNotificationContext, result?: CommandResponse): string {
  const presentation = buildCommandPresentation(command, context)
  if (result?.error) return result.error
  return presentation.notification.errorMessage ?? DEFAULT_ERROR_MESSAGE
}
